// components/puntos/flyer/FlyerPreview.tsx — CON-TOOLS-02 v1 (Oct 27, 2026). The contractor's flyer, drawn in the browser as SVG.
// Frame = GM-FLY-11-001 PRINT v5.3 (make_front_angel.py): same page box (666 × 468 pt = 9.25 × 6.5 in with bleed), same grid
// (L 45 · R 621 · B 45 · T 423, right column 451–621), storm-slate gradient + quiet rain, DM Serif wordmark with Surge "Maestro",
// headline 44.5/46.5, QR card 98 pt + brass CTA, 4:5 portrait with hairline edge, brass vetted check, "Your Yard, Mastered." sign-off.
// Back = the v4/v5.3 paper back. Coordinates below are the generator's (y measured from the bottom) converted with top = 468 − y.
// GM owns frame, copy and claims (D1); only template text + his choices come in. v1.7 naming: "Green Maestro contractor",
// first name + last initial, "Vetted Contractors · Verified Work". The QR is a stand-in; the print file carries the real code.
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';

export type FlyerKey = 'A_spotlight' | 'B_proof' | 'C_prices' | 'D_offer';
export interface FlyerPrice { label: string; from_cents: number | null; note?: string | null; quote?: boolean; asksStories?: boolean }   // quote = "price after a visit"
export interface FlyerData {
  template: FlyerKey;
  headline: string;            // the chosen GM headline (English on the card)
  subline: string;
  name: string;                // "Carlos R." (or the owner-approved company name)
  descriptor?: string | null;  // his trade, else "Local owner-operator"
  portraitUrl?: string | null; // A
  beforeUrl?: string | null;   // B
  afterUrl?: string | null;    // B
  prices?: FlyerPrice[];       // C front + the back offer list
  priceNote?: string | null;   // "Sample prices · ZIP 94105" (preview only, never printed)
  zip?: string | null;
  area?: string | null;        // "Snohomish, WA"
  highlights?: string[];       // owner-approved lines for this contractor (back panel)
  also?: string[];             // his other títulos: "Also from {name}: Hardscape"
  mailMonth?: number;          // 1–12: picks the 00013 §2.2 seasonal look (default November)
  productKey?: string | null;  // a seasonal product pins its own month (Christmas lights → December)
}

const W = 666, H = 468, L = 45, B = 45, T = 423, RX = 451, RW = 170, PH = RW * 5 / 4;
const top = (y: number) => H - y;   // generator y (from bottom) → SVG y
const F = { serif: "'DM Serif Display', Georgia, serif", sans: "'DM Sans', system-ui, sans-serif", mono: "'IBM Plex Mono', 'DM Mono', ui-monospace, monospace" };
const BRASS = '#C4A97D', SURGE = '#A3FF12';

// Colour themes — 00013 §2.2 "The Seasonal Accent" (one accent per month, marketing surfaces only). The front is a dark gradient from
// the mail month's accent (white text keeps ≥ 4.5:1); December / Christmas lights = blue hour + Glow gold. Mirrors supabase/functions/gm-vecinos-print/render.ts.
const ACCENTS: Record<number, string> = { 1: '#B8C5D0', 2: '#8B7355', 3: '#7FA87A', 4: '#D4A0B0', 5: '#5A7A4F', 6: '#D4B870', 7: '#C77B4A',
  8: '#6B8E5A', 9: '#C4973D', 10: '#B8652A', 11: '#6B7B8A', 12: '#D4AA5A' };
const PRODUCT_MONTH: Record<string, number> = { 'product:christmas-lights': 12 };
type RGB = [number, number, number];
const hx = (h: string): RGB => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)) as RGB;
const mix = (a: RGB, b: RGB, t: number): RGB => a.map((x, i) => Math.round(x + (b[i] - x) * t)) as RGB;
const lum = (c: RGB) => { const f = (v: number) => { v /= 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
  return 0.2126 * f(c[0]) + 0.7152 * f(c[1]) + 0.0722 * f(c[2]); };
const hs = (c: RGB) => `#${c.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
export interface FlyerTheme { month: number; top: string; bottom: string; accent: string; cta: string; texture: 'glow' | 'rain' | 'lines'; tint: string }
export function themeFor(d: Pick<FlyerData, 'mailMonth' | 'productKey'>): FlyerTheme {
  const m = (d.productKey && PRODUCT_MONTH[d.productKey]) || d.mailMonth || 11, acc = ACCENTS[m];
  if (m === 12) return { month: m, top: '#2B3D63', bottom: '#0D1424', accent: acc, cta: acc, texture: 'glow', tint: '#F6EEDC' };
  const a = hx(acc), ch = hx('#1A1A18'); let t = 0.2;
  while (1.05 / (lum(mix(a, ch, t)) + 0.05) < 4.5 && t < 0.9) t += 0.05;
  return { month: m, top: hs(mix(a, ch, t)), bottom: hs(mix(a, ch, 0.9)), accent: acc, cta: BRASS, texture: m === 11 ? 'rain' : 'lines', tint: hs(mix(a, [255, 255, 255], 0.86)) };
}
const money = (c: number | null) => (c == null ? '—' : c % 100 === 0 ? `$${(c / 100).toLocaleString('en-US')}` : `$${(c / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const priceText = (p: FlyerPrice) => money(p.from_cents);
/** Owner Oct 29: every line "price after a visit" → the card asks for a design visit (same rule as the print generator). */
const ctaText = (d: FlyerData) => ((d.prices ?? []).length > 0 && (d.prices ?? []).every((x) => x.quote) ? 'Scan to book a design visit.' : 'Scan for your price.');

/** Text width in pt for a given font (canvas measure once fonts are ready; a safe estimate before). */
let ctx: CanvasRenderingContext2D | null = null;
function textW(text: string, font: string, size: number): number {
  try { ctx = ctx ?? document.createElement('canvas').getContext('2d'); } catch { ctx = null; }
  if (!ctx) return text.length * size * 0.62;
  ctx.font = `${size}px ${font}`; return ctx.measureText(text).width;
}
function useFontsReady() {
  const [n, setN] = useState(0);
  useEffect(() => { let live = true; document.fonts?.ready.then(() => live && setN((x) => x + 1)).catch(() => {}); return () => { live = false; }; }, []);
  return n;
}
/** Two lines, broken after the first comma/period when there is one, else balanced by width. */
function twoLines(h: string): [string, string] {
  const w = h.trim().split(/\s+/); if (w.length < 2) return [h.trim(), ''];
  let best: [string, string] = [h.trim(), ''], score = Infinity;
  for (let i = 1; i < w.length; i++) {
    const a = w.slice(0, i).join(' '), b = w.slice(i).join(' ');
    const s = Math.abs(a.length - b.length) - (/[,.?!]$/.test(w[i - 1]) ? 6 : 0);   // prefer breaking after punctuation, then balance
    if (s < score) { score = s; best = [a, b]; }
  }
  return best;
}

function QrStandIn({ x, y, size }: { x: number; y: number; size: number }) {
  const n = 37, m = size / n, cells: ReactNode[] = [];
  const fin = (cx: number, cy: number) => (cx < 7 && cy < 7) || (cx >= n - 7 && cy < 7) || (cx < 7 && cy >= n - 7);
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (!fin(c, r) && (((c * 73856093) ^ (r * 19349663) ^ (c * r * 83492791)) >>> 0) % 7 < 3) cells.push(<rect key={`${r}-${c}`} x={x + c * m} y={y + r * m} width={m + 0.02} height={m + 0.02} />);
  const Fin = ({ cx, cy }: { cx: number; cy: number }) => (<g>
    <rect x={x + cx * m} y={y + cy * m} width={7 * m} height={7 * m} /><rect x={x + (cx + 1) * m} y={y + (cy + 1) * m} width={5 * m} height={5 * m} fill="#fff" />
    <rect x={x + (cx + 2) * m} y={y + (cy + 2) * m} width={3 * m} height={3 * m} /></g>);
  return <g fill="#111312">{cells}<Fin cx={0} cy={0} /><Fin cx={n - 7} cy={0} /><Fin cx={0} cy={n - 7} /></g>;
}

function Check({ x, y, color = BRASS }: { x: number; y: number; color?: string }) {   // generator: moveTo(RX+.5, vy+3.4) lineTo(RX+3, vy+1) lineTo(RX+7.6, vy+6.6)
  return <path d={`M${x + 0.5} ${top(y + 3.4)} L${x + 3} ${top(y + 1)} L${x + 7.6} ${top(y + 6.6)}`} stroke={color} strokeWidth={1.1} fill="none" strokeLinecap="round" strokeLinejoin="round" />;
}

// Christmas lights card: the Lit Up landing page's C9 string in Maestro green + red (mirrors gm-vecinos-print/render.ts stringLights)
const LIGHTS = ['#A3FF12', '#E24B4A'];
function StringLights() {
  const y0 = 24, xa = 9 + 12, xb = W - 9 - 12, n = 22, out: ReactNode[] = [];
  const bulb = (x: number, y: number, col: string, sz: number, key: string) => (
    <g key={key}>
      {[5, 4, 3, 2, 1].map((k) => <circle key={k} cx={x} cy={y + sz * 0.8} r={sz * (1 + k * 0.45)} fill={col} opacity={0.04 + (5 - k) * 0.03} />)}
      <rect x={x - sz * 0.42} y={y - sz * 0.35} width={sz * 0.84} height={sz * 0.75} fill="#7A7A7A" />
      <ellipse cx={x} cy={y + sz * 0.75} rx={sz * 0.6} ry={sz * 0.85} fill={col} />
      <ellipse cx={x - sz * 0.22} cy={y + sz * 0.55} rx={sz * 0.13} ry={sz * 0.33} fill="#fff" opacity={0.45} />
    </g>);
  out.push(<line key="w" x1={xa} x2={xb} y1={y0 - 3} y2={y0 - 3} stroke="#3A3A3A" strokeWidth={0.9} />);
  for (let i = 0; i < n; i++) {
    const x = xa + i * (xb - xa) / (n - 1);
    if (x > L + 125 && x < RX - 15 && (i % 2 === 0 || i % 3 === 0)) {
      const drops = 1 + (i * 7) % 3;
      for (let d = 0; d < drops; d++) {
        const yd = y0 + 19 + d * 18;
        out.push(<line key={`l${i}-${d}`} x1={x} x2={x} y1={yd - 18} y2={yd - 3} stroke="#3A3A3A" strokeWidth={0.6} />);
        out.push(bulb(x, yd, LIGHTS[(i + d + 1) % 2], 4.6 - d * 0.3, `d${i}-${d}`));
      }
    }
    out.push(bulb(x, y0, LIGHTS[i % 2], 5.4, `b${i}`));
  }
  return <g>{out}</g>;
}

export function FlyerFront({ d, uid }: { d: FlyerData; uid: string }) {
  const fonts = useFontsReady();
  const th = themeFor(d), CTA = th.cta;
  const [l1, l2] = useMemo(() => twoLines(d.headline.toUpperCase()), [d.headline]);
  // 44.5 pt like v5.3; smaller only if a line would run into the right column (measured on the rendered SVG text)
  const headRef = useRef<SVGTextElement>(null);
  const [head, setHead] = useState(44.5);
  useEffect(() => { setHead(44.5); }, [l1, l2, fonts]);
  useLayoutEffect(() => {
    const el = headRef.current; if (!el || head !== 44.5) return;
    let w = 0; try { w = el.getBBox().width; } catch { w = Math.max(textW(l1, F.serif, 44.5), textW(l2, F.serif, 44.5)); }
    const maxW = RX - 20 - L; if (w > maxW) setHead(Math.max(28, 44.5 * maxW / w));
  });
  const lead = head * 46.5 / 44.5, SUB_Y = T - PH;
  const nameSize = useMemo(() => Math.min(17.5, RW / Math.max(1, textW(d.name, F.serif, 1))), [d.name, fonts]);   // eslint-disable-line react-hooks/exhaustive-deps
  const py = T - PH, ly = py - 19;
  const clip = `gmf-clip-${uid}`, grad = `gmf-grad-${uid}`;
  const rain: ReactNode[] = [];
  for (let k = -30; k < 40; k++) { const x0 = k * 21; rain.push(<line key={k} x1={x0} y1={H} x2={x0 + H * 0.36} y2={0} />); }

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={`Flyer front: ${d.headline}`} style={{ display: 'block', borderRadius: 8, boxShadow: '0 6px 24px rgba(0,0,0,.18)' }}>
      <defs>
        <linearGradient id={grad} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={th.top} /><stop offset="1" stopColor={th.bottom} /></linearGradient>
        <clipPath id={clip}><rect x={RX} y={top(T)} width={RW} height={PH} rx={5} /></clipPath>
      </defs>
      <rect width={W} height={H} fill={`url(#${grad})`} />
      {th.texture === 'glow'
        ? <g>{Array.from({ length: 46 }, (_, i) => {   // sparkling four-point stars (rays up, down, left, right)
            const r = (n: number) => ((Math.sin(i * 12.9898 + n * 78.233) * 43758.5453) % 1 + 1) % 1;
            const x = r(1) * W, y = r(2) * H * 0.7, ln = 2 + r(3) * 4.5, w = ln * 0.13;
            if (x > L - 6 && x < RX - 8 && y > H - (T - 60) && y < H - B + 6 && r(5) < 0.6) return null;
            if (x > RX - 14 && x < 621 + 14 && y > H - T - 6) return null;   // never on his portrait or name block
            if (x > L - 8 && x < L + 125 && y < H - T + 26) return null;   // never on the wordmark
            const pts = [[x, y - ln], [x + w, y - w], [x + ln, y], [x + w, y + w], [x, y + ln], [x - w, y + w], [x - ln, y], [x - w, y - w]].map((q) => q.join(',')).join(' ');
            return <g key={i}><polygon points={pts} fill={r(6) < 0.55 ? '#fff' : th.accent} opacity={0.35 + r(4) * 0.5} />
              {ln > 4.5 && <circle cx={x} cy={y} r={ln * 0.28} fill="#fff" opacity={0.25} />}</g>; })}</g>
        : <g stroke="rgba(255,255,255,.035)" strokeWidth={0.45}>{rain}</g>}

      {d.productKey === 'product:christmas-lights' && <StringLights />}
      <text x={L} y={top(T - 13.3)} fontFamily={F.serif} fontSize={19} fill="#fff">Green <tspan fill={SURGE}>Maestro</tspan></text>

      <text ref={headRef} fontFamily={F.serif} fontSize={head} fill="#fff">
        <tspan x={L - 1.6} y={top(SUB_Y + 30 + lead)}>{l1}</tspan>
        <tspan x={L - 1.6} y={top(SUB_Y + 30)}>{l2}</tspan>
      </text>
      <text x={L} y={top(SUB_Y)} fontFamily={F.sans} fontWeight={500} fontSize={13} fill="rgba(255,255,255,.86)">{d.subline}</text>

      <rect x={L} y={top(B + 98)} width={98} height={98} rx={7} fill="#fff" />
      <QrStandIn x={L + 9} y={top(B + 98) + 9} size={80} />
      <text x={L + 114} y={top(B + 98 - 10)} fontFamily={F.sans} fontWeight={600} fontSize={14} fill={CTA}>{ctaText(d)}</text>
      <text x={L + 114} y={top(B + 98 - 27)} fontFamily={F.sans} fontSize={11.5} fill="rgba(255,255,255,.78)">Book in 90 seconds.</text>
      <text x={L + 114} y={top(B + 2)} fontFamily={F.mono} fontSize={7} letterSpacing={0.7} fill="rgba(255,255,255,.5)">GREENMAESTRO.COM</text>

      {/* right column: the only part that changes between archetypes */}
      {d.template === 'A_spotlight' && (
        <g clipPath={`url(#${clip})`}>
          <rect x={RX} y={top(T)} width={RW} height={PH} fill="#2C333B" />
          {d.portraitUrl && <image href={d.portraitUrl} x={RX} y={top(T)} width={RW} height={PH} preserveAspectRatio="xMidYMid slice" />}
        </g>
      )}
      {d.template === 'B_proof' && (
        <g>
          <g clipPath={`url(#${clip})`}>
            <rect x={RX} y={top(T)} width={RW} height={PH} fill="#2C333B" />
            {d.afterUrl && <image href={d.afterUrl} x={RX} y={top(T)} width={RW} height={PH} preserveAspectRatio="xMidYMid slice" />}
          </g>
          <rect x={RX + 8} y={top(py + 8 + 62)} width={78} height={62} rx={3} fill="#2C333B" stroke="#fff" strokeWidth={1.2} />
          {d.beforeUrl && <image href={d.beforeUrl} x={RX + 8} y={top(py + 8 + 62)} width={78} height={62} preserveAspectRatio="xMidYMid slice" />}
          {[['BEFORE', RX + 12, py + 8 + 62 - 10], ['AFTER', RX + RW - 36, T - 12]].map(([t, x, y]) => (
            <g key={t as string}><rect x={(x as number) - 3} y={top(y as number) - 7} width={(t as string).length * 4.9 + 6} height={10} rx={2} fill="rgba(14,18,12,.78)" />
              <text x={x as number} y={top(y as number)} fontFamily={F.mono} fontSize={6.5} letterSpacing={0.65} fill="#fff">{t}</text></g>
          ))}
        </g>
      )}
      {(d.template === 'C_prices' || d.template === 'D_offer') && (
        <g>
          <rect x={RX} y={top(T)} width={RW} height={PH} rx={5} fill="rgba(255,255,255,.06)" />
          <text x={RX + 14} y={top(T - 26)} fontFamily={F.mono} fontSize={7} letterSpacing={0.7} fill={CTA}>{(d.prices ?? []).some((x) => x.quote) ? 'WHAT WE BUILD' : 'YOUR PRICE, FROM'}</text>
          {(d.prices ?? []).slice(0, 4).map((p, i) => (
            <g key={p.label}>
              <text x={RX + 14} y={top(T - 56 - i * 34)} fontFamily={F.sans} fontWeight={600} fill="#fff"
                fontSize={Math.max(7, Math.min(11, 11 * (RW - 28 - (p.quote ? 0 : priceText(p).length * 6.6 + 8)) / Math.max(1, textW(p.label, F.sans, 11))))}>{p.label}</text>
              {p.quote && <text x={RX + 14} y={top(T - 66 - i * 34)} fontFamily={F.mono} fontSize={5.5} letterSpacing={0.55} fill={CTA}>PRICE AFTER A VISIT</text>}
              <text x={RX + RW - 14} y={top(T - 56 - i * 34)} textAnchor="end" fontFamily={F.mono} fontWeight={600} fontSize={11} fill={CTA}>{p.quote ? '' : priceText(p)}</text>
              <line x1={RX + 14} x2={RX + RW - 14} y1={top(T - 66 - i * 34)} y2={top(T - 66 - i * 34)} stroke="rgba(255,255,255,.12)" strokeWidth={0.5} />
            </g>
          ))}
          {d.priceNote && <text x={RX + 14} y={top(py + 12)} fontFamily={F.mono} fontSize={5.5} letterSpacing={0.4} fill="rgba(255,255,255,.55)">{d.priceNote.toUpperCase()}</text>}
        </g>
      )}
      <rect x={RX} y={top(T)} width={RW} height={PH} rx={5} fill="none" stroke="rgba(255,255,255,.22)" strokeWidth={0.6} />

      <text x={RX} y={top(ly)} fontFamily={F.mono} fontSize={7} letterSpacing={0.7} fill={CTA}>LOCAL INDEPENDENT CONTRACTOR</text>
      <text x={RX} y={top(ly - 20)} fontFamily={F.serif} fontSize={nameSize} fill="#fff">{d.name}</text>
      <text x={RX} y={top(ly - 35)} fontFamily={F.sans} fontSize={10.5} fill="rgba(255,255,255,.74)">{d.descriptor || 'Local owner-operator'}</text>
      <Check x={RX} y={ly - 50} color={CTA} />
      <text x={RX + 12} y={top(ly - 50)} fontFamily={F.sans} fontWeight={500} fontSize={10.5} fill={CTA}>Vetted by Green Maestro</text>

      <line x1={RX} x2={RX + 26} y1={top(B + 24)} y2={top(B + 24)} stroke={CTA} strokeOpacity={0.85} strokeWidth={0.6} />
      <text x={RX} y={top(B + 2)} fontFamily={F.serif} fontStyle="italic" fontSize={17} fill="#fff">Your Yard, Mastered.</text>
    </svg>
  );
}

/** Greedy word wrap to a width in points (canvas measure, estimate before fonts load). */
function wrapText(text: string, font: string, size: number, width: number): string[] {
  const out: string[] = []; let line = '';
  for (const w of text.split(/\s+/)) { const t = line ? `${line} ${w}` : w; if (line && textW(t, font, size) > width) { out.push(line); line = w; } else line = t; }
  return line ? [...out, line] : out;
}

export function FlyerBack({ d }: { d: FlyerData }) {
  const items = (d.prices ?? []).slice(0, 3);
  // the intro wraps to the panel (a long company name pushed it across the fold line); the list moves down by what it grew
  const intro: Array<{ text: string; y: number; size: number; bold?: boolean; green?: boolean }> = [];
  const PW = 281, rest = 'is an independent local business, vetted by Green Maestro.';
  let y = 84;
  if (textW(`${d.name} ${rest}`, F.sans, 9.5) <= PW) { intro.push({ text: d.name, y, size: 9.5, bold: true }); }
  else { for (const l of wrapText(d.name, F.sans, 9.5, PW)) { intro.push({ text: l, y, size: 9.5, bold: true }); y += 12; } }
  const restLine = textW(`${d.name} ${rest}`, F.sans, 9.5) <= PW ? null : rest;
  if (restLine) intro.push({ text: restLine, y, size: 9.5 });
  for (const l of wrapText('Need trees, irrigation or fencing too? Green Maestro has vetted contractors for those.', F.sans, 9.5, PW)) { y += 12; intro.push({ text: l, y, size: 9.5 }); }
  const dy = Math.min(26, Math.max(0, y - 119));
  const first = intro[0];
  if (!restLine && first) first.text = `${d.name}\u00A0`;   // rendered with the rest on the same line below
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Flyer back" style={{ display: 'block', borderRadius: 8, boxShadow: '0 6px 24px rgba(0,0,0,.18)', background: '#FAFAF7' }}>
      <rect width={W} height={H} fill="#FAFAF7" />
      <text x={30} y={45} fontFamily={F.serif} fontSize={19} fill="#1A1A18">Green <tspan fill="#74B80E">Maestro</tspan></text>
      <text x={30} y={58} fontFamily={F.mono} fontSize={6.5} fill="#5C5A55">greenmaestro.com{d.area || d.zip ? `  ·  ${[d.area, d.zip].filter(Boolean).join(' ')}` : ''}</text>
      <line x1={30} x2={310} y1={68} y2={68} stroke="#E3DFD7" strokeWidth={0.6} />
      {/* D6: on a puntos-funded card the network backs him; it never competes with him */}
      {!restLine && <text x={30 + textW(`${d.name} `, F.sans, 9.5)} y={84} fontFamily={F.sans} fontSize={9.5} fill="#1A1A18">{rest}</text>}
      {intro.map((l, k) => (
        <text key={k} x={30} y={l.y} fontFamily={F.sans} fontSize={l.size} fontWeight={l.bold ? 700 : 400} fill={l.green ? '#536B52' : '#1A1A18'}>{l.text}</text>
      ))}
      <text x={30} y={130 + dy} fontFamily={F.mono} fontSize={6.5} fill="#5C5A55">Pick what your yard needs</text>
      {items.map((p, i) => (
        <g key={p.label}>
          <rect x={30} y={145 + i * 40 + dy} width={9} height={9} fill="none" stroke="#1A1A18" strokeWidth={1.1} />
          <text x={46} y={153 + i * 40 + dy} fontFamily={F.sans} fontWeight={600} fontSize={10.5} fill="#1A1A18">{p.label}</text>
          {!p.quote && <text x={311} y={153 + i * 40 + dy} textAnchor="end" fontFamily={F.mono} fontWeight={700} fontSize={10} fill="#1A1A18">from {money(p.from_cents)}</text>}
          {p.quote && <text x={46} y={164 + i * 40 + dy} fontFamily={F.sans} fontSize={8} fill="#5C5A55">Price after a design visit</text>}
          {p.note && <text x={46} y={165 + i * 40 + dy} fontFamily={F.sans} fontSize={8} fill="#5C5A55">{p.note}</text>}
        </g>
      ))}
      {(d.highlights?.length ?? 0) > 0 && (
        <g>
          <rect x={30} y={280} width={281} height={92} rx={6} fill={themeFor(d).tint} />
          <text x={40} y={296} fontFamily={F.mono} fontSize={6.5} fill="#5C5A55">{`WHY ${d.name.toUpperCase()}`.slice(0, 60)}</text>
          {d.highlights!.slice(0, 3).map((h, i) => (
            <g key={i}><text x={40} y={312 + i * 20} fontFamily={F.sans} fontSize={8.6} fill="#1A1A18">✓</text>
              <foreignObject x={52} y={304 + i * 20} width={252} height={20}>
                <div style={{ font: `8.6px/10px ${F.sans}`, color: '#1A1A18' }}>{h}</div>
              </foreignObject></g>
          ))}
        </g>
      )}
      <text x={30} y={396} fontFamily={F.sans} fontWeight={700} fontSize={10} fill="#1A1A18">{ctaText(d)}</text>
      {items.some((p) => p.asksStories) && <text x={30} y={410} fontFamily={F.sans} fontSize={8} fill="#5C5A55">Bigger lots and 2- and 3-story houses cost a little more.</text>}
      <text x={30} y={425} fontFamily={F.mono} fontSize={6.5} fill="#5C5A55">Text (360) 777-5440</text>
      <text x={30} y={437} fontFamily={F.mono} fontSize={6.5} fill="#5C5A55">Vetted Contractors · Verified Work</text>
      <QrStandIn x={258} y={382} size={50} />
      <line x1={332} x2={332} y1={24} y2={444} stroke="#E3DFD7" strokeWidth={0.6} />
      <rect x={548} y={31} width={95} height={55} fill="none" stroke="#1A1A18" strokeWidth={1} />
      <text fontFamily={F.sans} fontWeight={700} fontSize={6.5} fill="#1A1A18" textAnchor="middle">
        {['PRSRT STD', 'ECRWSS', 'U.S.POSTAGE', 'PAID', 'EDDM Retail'].map((s, i) => <tspan key={s} x={595.5} y={43 + i * 9}>{s}</tspan>)}
      </text>
      <text x={373} y={262} fontFamily={F.sans} fontWeight={700} fontSize={9} fill="#1A1A18">Local Postal Customer</text>
    </svg>
  );
}

let seq = 0;
/** Front, tap to flip to the back. */
export default function FlyerPreview({ d, side, onFlip, flipLabel }: { d: FlyerData; side: 'front' | 'back'; onFlip?: () => void; flipLabel?: string }) {
  const [uid] = useState(() => `f${++seq}`);
  return (
    <button type="button" onClick={onFlip} aria-label={flipLabel} style={{ all: 'unset', display: 'block', width: '100%', cursor: onFlip ? 'pointer' : 'default' }}>
      {side === 'front' ? <FlyerFront d={d} uid={uid} /> : <FlyerBack d={d} />}
    </button>
  );
}
